import { useEffect, useRef, type Dispatch, type SetStateAction } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getAuditExportDownload } from "../../../api/admin-audit-api.js";
import { AdminApiError } from "../../../api/admin-users-api.js";
import {
  announceAuditExportClaimed,
  useAuditExportDownload,
} from "../../../store/audit-export-download.js";
import type { ExportState } from "./audit-query.js";
import { useDownloadGuard } from "./download-guard.js";

/* An export id is a v4 uuid, the only shape the server's ParseUUIDPipe takes.
   Checked before any request: encodeURIComponent keeps `.` and `..`, and a
   hand-edited link such as `?export=..` would otherwise re-route the grant
   request it starts. */
const EXPORT_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const NOT_FOUND = "This export couldn't be found.";

/**
 * The bell notification and the email both link to
 * `/admin/audit?export=<jobId>`. This downloads that export ONCE and removes
 * the parameter, so a refresh, a tab restore or browser Back never downloads it
 * again — and renders nothing. A failure shows in the page's own inline status
 * area, never as anything floating.
 *
 * Rendered by AuditAdmin only inside a router, and BEFORE
 * AuditExportAutoDownload, so that when the linked job is also the watched one
 * this claims it first and the watcher's re-check skips it (one file, not two).
 */
export function AuditExportLinkHandler({
  onExportState,
}: {
  readonly onExportState: Dispatch<SetStateAction<ExportState>>;
}): null {
  const location = useLocation();
  const navigate = useNavigate();
  const exportId = new URLSearchParams(location.search).get("export");
  /* The job being handled. Reset once the parameter is gone, so a second
     click on the same notification downloads again, while StrictMode's re-run
     of the same render is still skipped. */
  const handled = useRef<string | null>(null);
  /* The failure this handler last put on the line, by identity, so a later
     link that downloads clears that one and nothing else (an Export CSV
     failure stays). */
  const shownFailure = useRef<ExportState | null>(null);
  const beginDownload = useDownloadGuard();

  useEffect(() => {
    if (!exportId) {
      handled.current = null;
      return;
    }
    if (handled.current === exportId) return;
    handled.current = exportId;

    const download = useAuditExportDownload.getState();
    if (download.jobId === exportId) download.clear();

    const remaining = new URLSearchParams(location.search);
    remaining.delete("export");
    const search = remaining.toString();
    navigate(
      {
        pathname: location.pathname,
        search: search ? `?${search}` : "",
        hash: location.hash,
      },
      { replace: true },
    );
    const showFailure = (message: string): void => {
      const failure: ExportState = { status: "failed", message };
      shownFailure.current = failure;
      onExportState(failure);
    };
    if (!EXPORT_ID_RE.test(exportId)) {
      showFailure(NOT_FOUND);
      return;
    }
    /* Another tab may be watching this same job; it must not download it too. */
    announceAuditExportClaimed(exportId);
    /* Not cancelled on cleanup: the strip above re-runs this effect, and a
       "cancelled" flag would swallow the download it started. What drops a
       late answer is the guard — this page gone, or the session changed. */
    const stillCurrent = beginDownload();
    void getAuditExportDownload(exportId).then(
      (grant) => {
        if (!stillCurrent()) return;
        /* An earlier link's failure no longer applies; anything else on the
           line (an export just started, an Export CSV failure) stays. */
        onExportState((current) =>
          current === shownFailure.current ? { status: "idle" } : current,
        );
        window.location.assign(grant.url);
      },
      (error: unknown) => {
        if (!stillCurrent()) return;
        showFailure(linkErrorMessage(error));
      },
    );
  }, [
    exportId,
    location.pathname,
    location.search,
    location.hash,
    navigate,
    onExportState,
    beginDownload,
  ]);

  return null;
}

function linkErrorMessage(error: unknown): string {
  const code = error instanceof AdminApiError ? error.code : undefined;
  if (code === "AUDIT_EXPORT_EXPIRED") {
    return "This export has expired. Export again to get a new file.";
  }
  if (code === "AUDIT_EXPORT_NOT_READY") {
    return "This export isn't ready to download.";
  }
  if (code === "AUDIT_EXPORT_NOT_FOUND") return NOT_FOUND;
  /* A network error, a 5xx or any other refusal: the export may well exist. */
  return "Couldn't download this export. Open the link again to retry.";
}
